import React, { useState, useEffect } from 'react';
import axios from 'axios';

const SENDTRIC_PREFIX = 'https://gen.sendtric.com/countdown/';

const CrearCampana = ({ campana, onClose, onSuccess }) => {
  const isEdit = !!campana;
  const isActiva = campana?.estado === 'activa';

  const [form, setForm] = useState({
    nombre: campana?.nombre || '',
    codigo: campana?.codigo || '',
    tipo_descuento: campana?.tipo_descuento || 'porcentaje',
    valor_descuento: campana?.valor_descuento ?? '',
    tope_maximo: campana?.tope_maximo ?? '',
    fecha_inicio: campana?.fecha_inicio || '',
    duracion_contador_dias: campana?.duracion_contador_dias ?? 10,
    asunto: campana?.asunto || 'Tu descuento exclusivo de FilmSolar',
    countdown_url: campana?.countdown_url || '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  useEffect(() => { setImgError(false); }, [form.countdown_url]);

  const isValidCountdownUrl = form.countdown_url.startsWith(SENDTRIC_PREFIX);
  const showCountdownPreview = form.countdown_url.length > 0 && isValidCountdownUrl;

  const validate = () => {
    const e = {};
    if (!form.nombre.trim()) e.nombre = 'Requerido';
    if (!form.codigo.trim()) e.codigo = 'Requerido';
    else if (!/^[A-Z0-9]{1,30}$/.test(form.codigo)) e.codigo = 'Solo letras mayúsculas y números, máx 30 chars';
    if (form.valor_descuento === '' || form.valor_descuento === null) e.valor_descuento = 'Requerido';
    else if (Number(form.valor_descuento) <= 0) e.valor_descuento = 'Debe ser mayor a 0';
    else if (form.tipo_descuento === 'porcentaje' && Number(form.valor_descuento) > 100) e.valor_descuento = 'Máximo 100%';
    if (!form.fecha_inicio) e.fecha_inicio = 'Requerido';
    if (!form.duracion_contador_dias || Number(form.duracion_contador_dias) <= 0) e.duracion_contador_dias = 'Debe ser mayor a 0';
    if (form.countdown_url && !isValidCountdownUrl) e.countdown_url = 'Debe comenzar con https://gen.sendtric.com/countdown/';
    return e;
  };

  const handleNext = () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setErrors({});
    setConfirming(true);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      const payload = {
        nombre: form.nombre.trim(),
        codigo: form.codigo.toUpperCase(),
        tipo_descuento: form.tipo_descuento,
        valor_descuento: Number(form.valor_descuento),
        tope_maximo: form.tope_maximo !== '' ? Number(form.tope_maximo) : null,
        fecha_inicio: form.fecha_inicio,
        duracion_contador_dias: Number(form.duracion_contador_dias),
        asunto: form.asunto.trim() || 'Tu descuento exclusivo de FilmSolar',
        countdown_url: form.countdown_url.trim(),
      };
      if (isEdit) {
        await axios.patch(
          `${process.env.REACT_APP_BACKEND_URL}/api/campanas/${campana.id}/`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/campanas/`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      onSuccess();
    } catch (err) {
      const detail = err.response?.data;
      const msg = typeof detail === 'string'
        ? detail
        : Object.values(detail || {}).flat().join(' ') || 'Error al guardar';
      setErrors({ _server: msg });
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (field) => [
    'w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300',
    errors[field] ? 'border-red-400' : 'border-gray-300',
    isActiva && ['codigo', 'tipo_descuento', 'valor_descuento'].includes(field)
      ? 'bg-gray-100 cursor-not-allowed'
      : '',
  ].join(' ');

  /* ── Pantalla de confirmación ── */
  if (confirming) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl flex flex-col max-h-[90vh]">
          <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
            <h2 className="text-xl font-bold text-gray-900">Confirmar antes de guardar</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
          </div>

          <div className="p-6 space-y-5 overflow-y-auto flex-1">
            <p className="text-sm text-gray-600">
              Verifica que el contador del link esté configurado para <strong>{form.duracion_contador_dias} día{Number(form.duracion_contador_dias) !== 1 ? 's' : ''}</strong>, igual que la duración configurada en la campaña.
            </p>

            {/* Duración destacada */}
            <div className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-xl px-5 py-4">
              <div className="text-4xl font-extrabold text-orange-500">{form.duracion_contador_dias}</div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Duración del contador</p>
                <p className="text-sm font-semibold text-gray-800">
                  {Number(form.duracion_contador_dias) === 1 ? '1 día' : `${form.duracion_contador_dias} días`}
                </p>
              </div>
            </div>

            {/* Preview del countdown */}
            {form.countdown_url ? (
              <div className="space-y-3">
                <p className="text-xs text-gray-400 uppercase tracking-wide">Preview del contador en el email</p>
                <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50 flex flex-col items-center p-4 gap-3">
                  <img
                    src={form.countdown_url}
                    alt="Preview countdown"
                    className="max-h-28 rounded"
                    onError={() => setImgError(true)}
                    onLoad={() => setImgError(false)}
                  />
                  {imgError && (
                    <p className="text-red-500 text-xs text-center">La imagen no cargó — la URL puede ser incorrecta.</p>
                  )}
                  <p className="text-xs text-gray-400 break-all text-center">{form.countdown_url}</p>
                </div>
                {!imgError && (
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg px-4 py-3 text-xs text-yellow-800">
                    El GIF de arriba debe mostrar un contador de <strong>{form.duracion_contador_dias} día{Number(form.duracion_contador_dias) !== 1 ? 's' : ''}</strong>. Si no coincide, vuelve y ajusta el link de Sendtric.
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-4 text-sm text-gray-500 text-center">
                No se configuró URL de countdown — podrás agregarla después al editar la campaña.
              </div>
            )}

            {errors._server && (
              <p className="text-red-600 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">{errors._server}</p>
            )}
          </div>

          <div className="flex gap-3 justify-end p-6 border-t flex-shrink-0">
            <button
              onClick={() => setConfirming(false)}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
            >
              Volver al formulario
            </button>
            <button
              onClick={handleConfirm}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
              style={{ backgroundColor: '#FF743A' }}
            >
              {submitting ? 'Guardando...' : 'Confirmar y guardar'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Formulario ── */
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900">
            {isEdit ? 'Editar campaña' : 'Nueva campaña'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {errors._server && (
            <p className="text-red-600 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">{errors._server}</p>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
            <input
              type="text"
              value={form.nombre}
              onChange={set('nombre')}
              className={inputCls('nombre')}
              placeholder="Campaña Invierno 2026"
            />
            {errors.nombre && <p className="text-red-500 text-xs mt-1">{errors.nombre}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
            <input
              type="text"
              value={form.codigo}
              onChange={(e) => setForm((f) => ({ ...f, codigo: e.target.value.toUpperCase() }))}
              className={inputCls('codigo')}
              disabled={isActiva}
              placeholder="FILM2026"
            />
            {errors.codigo && <p className="text-red-500 text-xs mt-1">{errors.codigo}</p>}
            {isActiva && <p className="text-xs text-gray-400 mt-1">No editable mientras la campaña está activa.</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de descuento</label>
            <select
              value={form.tipo_descuento}
              onChange={set('tipo_descuento')}
              className={inputCls('tipo_descuento')}
              disabled={isActiva}
            >
              <option value="porcentaje">Porcentaje (%)</option>
              <option value="monto_fijo">Monto fijo (CLP)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valor del descuento {form.tipo_descuento === 'porcentaje' ? '(%)' : '(CLP)'}
            </label>
            <input
              type="number"
              value={form.valor_descuento}
              onChange={set('valor_descuento')}
              className={inputCls('valor_descuento')}
              disabled={isActiva}
              min="0"
            />
            {errors.valor_descuento && <p className="text-red-500 text-xs mt-1">{errors.valor_descuento}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tope máximo (opcional, CLP)</label>
            <input
              type="number"
              value={form.tope_maximo}
              onChange={set('tope_maximo')}
              className={inputCls('tope_maximo')}
              min="0"
              placeholder="Sin tope"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de inicio</label>
            <input
              type="date"
              value={form.fecha_inicio}
              onChange={set('fecha_inicio')}
              className={inputCls('fecha_inicio')}
            />
            {errors.fecha_inicio && <p className="text-red-500 text-xs mt-1">{errors.fecha_inicio}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duración del contador (días)</label>
            <input
              type="number"
              value={form.duracion_contador_dias}
              onChange={set('duracion_contador_dias')}
              className={inputCls('duracion_contador_dias')}
              min="1"
            />
            {errors.duracion_contador_dias && <p className="text-red-500 text-xs mt-1">{errors.duracion_contador_dias}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Asunto del email</label>
            <input
              type="text"
              value={form.asunto}
              onChange={set('asunto')}
              className={inputCls('asunto')}
              placeholder="Tu descuento exclusivo de FilmSolar"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">URL countdown (Sendtric)</label>
              <a
                href="https://app.sendtric.com/timers"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-orange-500 hover:text-orange-700 underline"
              >
                Crear contador en Sendtric →
              </a>
            </div>
            <input
              type="url"
              value={form.countdown_url}
              onChange={set('countdown_url')}
              className={inputCls('countdown_url')}
              placeholder="https://gen.sendtric.com/countdown/..."
            />
            {errors.countdown_url && <p className="text-red-500 text-xs mt-1">{errors.countdown_url}</p>}
            {showCountdownPreview && (
              <div className="mt-3 space-y-2">
                <img
                  src={form.countdown_url}
                  alt="Preview countdown"
                  className="rounded border border-gray-200 max-h-24"
                  onError={() => setImgError(true)}
                  onLoad={() => setImgError(false)}
                />
                {imgError && (
                  <p className="text-red-500 text-xs">La imagen no cargó — verifica que la URL sea correcta.</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 justify-end p-6 border-t flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleNext}
            className="px-4 py-2 rounded-lg text-white text-sm font-semibold"
            style={{ backgroundColor: '#FF743A' }}
          >
            {isEdit ? 'Revisar y guardar' : 'Revisar y crear'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CrearCampana;
